import React, { useRef } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { pageConfig } from '../config/page-titles';
import ABComparisonSlider from '../components/ABComparisonSlider';
import { SimpleResponsivePicture, SimpleResponsiveImage } from '../components/SimpleResponsiveImage';
import ImageCycle from '../components/ImageCycle';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../utils/examples.css';
import GlyphSvg from '../components/Glyphs';

// Register ScrollTrigger
gsap.registerPlugin(ScrollTrigger);

export default function Demo() {
  usePageTitle({
    title: pageConfig.demo.title,
    description: pageConfig.demo.description,
    image: pageConfig.demo.image
  });

  const galleryRef = useRef();

  // KNIX brand data
  const brand = {
    id: 'KNIX',
    logo: 'img/examples/knix/knix-logo-v2.webp',
    logoWidth: 180,
    brand: 'KNIX',
    headline: 'Colorway swap & Model Swap Demo',
    typeOfProduct: 'Intimates & Activewear',
    typeOfBusiness: 'DTC Ecommerce',
    services: [
      { glyph: 'g09', text: 'Model Swap' },
      { glyph: 'g11', text: 'Avatar Generation' },
      { glyph: 'g04', text: 'Diverse Representation' },
      { glyph: 'g05', text: 'Pose Variations' },
      { glyph: 'g08', text: 'Size Inclusivity' }
    ],
    description: 'Showcasing swapping product colorways onto existing photos, and model swaps into AI avatars.',
    gallery: [
      {
        type: 'ab-slider',
        size: 'S',
        beforeImage: 'img/examples/knix/color_swap/SWAP_C_00005_.png',
        afterImage: 'img/examples/knix/color_swap/SWAP_A_00003_.png',
        caption: 'Image to image colorway swap',
        minHeight: '75vh'
      },
      {
        type: 'static',
        size: 'S',
        src: 'img/examples/knix/color_swap/SWAP_B_00001_.png',
        caption: 'Reference image to swap',
        minHeight: '75vh'
      },

      {
        type: 'ab-slider',
        size: 'S',
        beforeImage: 'img/examples/knix/color_swap/SWAP_A_00011_.png',
        afterImage: 'img/examples/knix/color_swap/SWAP_A_00012_.png',
        caption: 'Image to image colorway swap',
        minHeight: '75vh'
      },
      {
        type: 'static',
        size: 'S',
        src: 'img/examples/knix/color_swap/SWAP_B_00003_.png',
        caption: 'Reference image to swap',
        minHeight: '75vh'
      },

      {
        type: 'ab-slider',
        size: 'S',
        beforeImage: 'img/examples/knix/color_swap/SWAP_C_00001_.png',
        afterImage: 'img/examples/knix/color_swap/SWAP_A_00014_.png',
        caption: 'Image to image colorway swap',
        minHeight: '75vh'
      },

      {
        type: 'ab-slider',
        size: 'S',
        beforeImage: 'img/examples/knix/color_swap/SWAP_C_00008_.png',
        afterImage: 'img/examples/knix/color_swap/SWAP_A_00019_.png',
        caption: 'Image to image colorway swap',
        minHeight: '75vh'
      },

      {
        type: 'ab-slider',
        size: 'S',
        beforeImage: 'img/examples/knix/compare/MomentaRacerbackSportsBra_Olive_04_A_0001.webp',
        afterImage: 'img/examples/knix/compare/MomentaRacerbackSportsBra_Olive_04_B_0001.webp',
        caption: 'Model swap to Avatar: Alex',
        minHeight: '75vh'
      },

      {
        type: 'ab-slider',
        size: 'S',
        beforeImage: 'img/examples/knix/compare/SculptRibCottonLongSleeveTop_Ivory_06_A_0001.webp',
        afterImage: 'img/examples/knix/compare/SculptRibCottonLongSleeveTop_Ivory_06_B_0001.webp',
        caption: 'Model swap to Avatar: Alex',
        minHeight: '75vh'
      },

      {
        type: 'ab-slider',
        size: 'S',
        beforeImage: 'img/examples/knix/compare/SweetDreamsNursingTank_Rosewater_01_A_0001.webp',
        afterImage: 'img/examples/knix/compare/SweetDreamsNursingTank_Rosewater_01_B_0001.webp',
        caption: 'Model swap to Avatar: Alex',
        minHeight: '75vh'
      },

      {
        type: 'ab-slider',
        size: 'S',
        beforeImage: 'img/examples/knix/compare/BlissFitNursingPumpingBra_Rosewater_05_A_0001.webp',
        afterImage: 'img/examples/knix/compare/BlissFitNursingPumpingBra_Rosewater_05_B_0001.webp',
        caption: 'Model swap to Avatar: Alex',
        minHeight: '75vh'
      },

    ]
  };

  const renderGalleryItem = (item, index) => {
    switch (item.type) {
      case 'ab-slider':
        return (
          <div className="gallery-item" style={{ width: '100%', overflow: 'hidden' }}>
            <ABComparisonSlider
              beforeImage={item.beforeImage}
              afterImage={item.afterImage}
              caption={item.caption}
              minHeight={item.minHeight}
              customHeight="60vh"
              customMinHeight="50vh"
              customMaxHeight="80vh"
            />
          </div>
        );
      
      case 'image':
        return (
          <div className="gallery-item" style={{ width: '100%', overflow: 'hidden' }}>
            <SimpleResponsivePicture
              src={item.src}
              alt={item.caption || 'Gallery image'}
              className="img-fluid"
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: 'var(--border-radius)',
                boxShadow: 'var(--shadow-main-box)'
              }}
              imgStyle={{
                width: '100%',
                height: 'auto',
                borderRadius: 'var(--border-radius)',
                objectFit: 'cover'
              }}
              widths={[400, 800, 1200]}
              sizes="(max-width: 768px) 100vw, 800px"
              quality={85}
            />
            {item.caption && (
              <p className="fs-8 text-muted mt-2 text-center">{item.caption}</p>
            )}
          </div>
        );
      
      case 'cycle':
        return (
          <div className="gallery-item" style={{ width: '100%', overflow: 'hidden' }}>
            <ImageCycle
              images={item.images}
              captions={item.captions}
              interval={3000}
            />
            {item.captions && item.captions[0] && (
              <p className="fs-8 text-muted mt-2 text-center">{item.captions[0]}</p>
            )}
          </div>
        );
      
      case 'static':
        const isVideo = item.src && (item.src.endsWith('.mp4') || item.src.endsWith('.webm') || item.src.endsWith('.mov'));
        return (
          <div className="gallery-item" style={{ width: '100%', overflow: 'hidden' }}>
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: item.minHeight || '60vh',
                minHeight: item.minHeight || (item.size === 'S' ? '50vh' : '60vh'),
                maxHeight: item.maxHeight || '80vh',
                overflow: 'hidden',
                borderRadius: 'var(--border-radius)',
                boxShadow: 'var(--shadow-main-box)'
              }}
            >
              {isVideo ? (
                <video
                  src={item.src}
                  autoPlay={item.autoPlay !== false}
                  loop={item.loop !== false}
                  muted={item.muted !== false}
                  playsInline
                  controls={item.controls === true}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: item.objectFit || 'cover',
                    objectPosition: item.objectPosition || 'center',
                    borderRadius: 'var(--border-radius)'
                  }}
                />
              ) : (
                <img
                  src={item.src}
                  alt={item.caption || 'Gallery media'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: item.objectFit || 'cover',
                    objectPosition: item.objectPosition || 'center',
                    borderRadius: 'var(--border-radius)'
                  }}
                />
              )}
            </div>
            {item.caption && (
              <p className="fs-8 text-muted mt-2 text-center">{item.caption}</p>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  const renderGallery = () => {
    const items = brand.gallery;
    const renderedItems = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const size = item.size || 'L'; // Default to large if no size specified
      
      if (size === 'L') {
        // Large items get full width
        renderedItems.push(
          <div key={`row-${i}`} className="mb-4">
            {renderGalleryItem(item, i)}
          </div>
        );
      } else if (size === 'S') {
        // Small items - check if next item is also small to group them
        if (i + 1 < items.length && items[i + 1].size === 'S') {
          // Create a row with two small items (responsive: 1 column on mobile, 2 on desktop)
          renderedItems.push(
            <div key={`row-${i}`} className="mb-4 gallery-small-pair">
              <div className="gallery-item" style={{ width: '100%', overflow: 'hidden' }}>
                {renderGalleryItem(item, i)}
              </div>
              <div className="gallery-item" style={{ width: '100%', overflow: 'hidden' }}>
                {renderGalleryItem(items[i + 1], i + 1)}
              </div>
            </div>
          );
          i++; // Skip the next item since we've already rendered it
        } else {
          // Single small item - full width on mobile, half width centered on desktop
          renderedItems.push(
            <div key={`row-${i}`} className="mb-4 gallery-single-small">
              <div className="gallery-item" style={{ width: '100%', overflow: 'hidden' }}>
                {renderGalleryItem(item, i)}
              </div>
            </div>
          );
        }
      }
    }
    
    return renderedItems;
  };

  return (
    <>
      <section className="container-fluid examples-page">
        <div className="py-4 py-lg-4 py-md-5"></div>
        
        {/* Brand Logo */}
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 text-center">
              <div className="brand-logo-container mb-5">
                <img
                  src={brand.logo}
                  alt={`${brand.brand} logo`}
                  className="img-fluid"
                  style={{
                    maxWidth: `${Math.min(brand.logoWidth * 0.8, 120)}px`,
                    height: 'auto',
                    maxHeight: '60px'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content - Split Layout */}
        <div className="container-fluid">
          <div className="row">
            {/* Left Column - Fixed Brand Info */}
            <div className="col-12 col-lg-4 order-1 order-lg-1 d-flex align-items-start">
              <div className="brand-info-fixed p-4" style={{ position: 'sticky', top: '100px' }}>
                <div className="mb-4">
                  <h2 className="fs-1 c-c00 f-a mb-0"><GlyphSvg glyph={'g01'} size={"14"} color={"var(--c00)"} /> {brand.brand}</h2>
                </div>
                
                <div className="brand-details mb-4">
                  <div className="d-flex flex-column">
                    <p className="fs-8 mb-1 f-b f-uc c-c2">{brand.typeOfProduct}</p>
                    <p className="fs-8 mb-0 f-b f-uc c-c2">{brand.typeOfBusiness}</p>
                  </div>
                </div>

                <h4 className="mb-4 f-b c-c00">{brand.headline}</h4>
                <p className="fs-7 text-muted mb-4">{brand.description}</p>
                
                <div>
                  <strong className="fs-8 f-b c-c2">Features Tested:</strong>
                                      <div className="mt-2">
                      {brand.services.map((service, serviceIndex) => {
                        const serviceText = typeof service === 'string' ? service : service.text;
                        const serviceGlyph = typeof service === 'object' && service.glyph ? service.glyph : null;
                        
                        return (
                          <span
                            key={serviceIndex}
                            className="f-b f-uc"
                            style={{
                              fontSize: '0.65rem',
                              padding: '0.375rem 0.75rem',
                              borderRadius: 'var(--border-radius)',
                              border: '1px solid var(--c00)',
                              color: 'var(--c00)',
                              display: 'inline-block',
                              marginRight: '0.5rem',
                              marginBottom: '0.5rem',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {serviceGlyph && (
                              <GlyphSvg 
                                glyph={serviceGlyph} 
                                size={"10"} 
                                color={"var(--c00)"} 
                                style={{ marginRight: '0.5rem', verticalAlign: 'middle', marginTop: '-3px' }}
                              />
                            )}
                            {serviceText}
                          </span>
                        );
                      })}
                    </div>
                </div>
              </div>
            </div>

            {/* Right Column - Gallery */}
            <div className="col-12 col-lg-8 order-2 order-lg-2 p-4">
              <div ref={galleryRef} className="gallery-container">
                {renderGallery()}
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="container mt-5">
          <div className="row justify-content-center">
            <div className="col-12 text-center">
              <h4 className="mb-4 f-uc">Ready to start iterating?</h4>
              <div className="d-flex flex-column flex-md-row gap-3 justify-content-center">
                <a className="btn b-c5 c-c0" href="/contact" data-discover="true">
                  Start Your Project
                </a>
                <a className="btn b-c0 c-c00 border border-c5" href="/pricing" data-discover="true">
                  View Pricing
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
} 